import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

const GRID_BG = {
  backgroundImage: 'linear-gradient(rgba(0,0,0,0.022) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.022) 1px, transparent 1px)',
  backgroundSize: '44px 44px',
};

const CASE_STUDIES = [
  {
    id: 'warehouse',
    icon: '📦',
    industryColor: '#C0492A',
    industryBg: '#F6E7E1',
    company: 'Kōwhai Logistics NZ — East Tamaki, Auckland',
    industry: 'Warehouse & Distribution',
    title: 'Streamlining sales & accounting with AI automation',
    challenge: 'Sales staff spent 40% of their time on routine stock and pricing questions, while accounting drowned in PDF supplier invoices, manual reconciliation, and chasing overdue accounts. Month-end reporting took three full days.',
    solution: [
      'AI voice agent handling sales inquiries, stock availability and delivery estimates',
      'Automatic invoice PDF extraction — line items, GST and totals straight to Excel',
      'Reconciliation against purchase orders with discrepancy flagging',
      'Accounts-receivable automation with email & SMS payment reminders',
      'One-click monthly financial summaries and cash-flow projections',
    ],
    results: [
      { metric: '85%', label: 'Less manual data entry' },
      { metric: '3 hrs', label: 'Month-end reporting (was 3 days)' },
      { metric: '$120K', label: 'Annual operational savings' },
      { metric: '99.5%', label: 'Invoice extraction accuracy' },
    ],
    quote: 'The invoice extraction alone saved us two full-time positions. What used to take hours now happens automatically — it pulls everything from PDFs, calculates totals, and even catches errors we used to miss.',
    author: 'Sarah M.',
    role: 'Finance Manager',
  },
  {
    id: 'property',
    icon: '🏠',
    industryColor: '#5E6B39',
    industryBg: '#EAEDE2',
    company: 'Tūī Property Group — 500+ Auckland properties',
    industry: 'Property Management',
    title: 'Effortless transaction & repair management',
    challenge: 'Beyond missing 30% of incoming calls, the team generated hundreds of invoices monthly, tracked contractor quotes buried in PDFs, reconciled maintenance expenses, and produced owner statements — entire weeks lost to paperwork.',
    solution: [
      'AI voice agent triaging maintenance calls and tenant inquiries 24/7',
      'Automated rent, fee and management invoices generated on schedule',
      'Contractor quote extraction with pricing comparison and cost roll-ups',
      'Owner statements auto-generated from multiple data sources',
      'Maintenance cost analysis with spending patterns and budget forecasts',
    ],
    results: [
      { metric: '95%', label: 'Call answer rate (up from 70%)' },
      { metric: '90%', label: 'Less invoice processing time' },
      { metric: '4 hrs', label: 'Average repair response time' },
      { metric: '500+', label: 'Invoices auto-generated monthly' },
    ],
    quote: 'Someone used to spend two days extracting numbers from quotes and invoices into spreadsheets. Now the AI does it instantly. Owner statements that took a week now generate in minutes.',
    author: 'James C.',
    role: 'Property Director',
  },
];

const CaseStudies = () => (
  <div style={{ background: '#ECEAE3', minHeight: '100vh', ...GRID_BG }}>
    <Header />

    <section style={{ maxWidth: 1000, margin: '0 auto', padding: '72px 40px 0', textAlign: 'center' }}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 9, background: '#fff', borderRadius: 999, padding: '9px 18px', fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', marginBottom: 24 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#5E6B39', display: 'inline-block' }} />
        PROVEN RESULTS
      </div>
      <h1 style={{ fontSize: 'clamp(2.2rem, 4.5vw, 3.5rem)', fontWeight: 800, letterSpacing: '-0.03em', margin: 0, lineHeight: 1.05, color: '#1A1A18' }}>
        Real businesses. Real numbers.
      </h1>
      <p style={{ fontSize: 20, lineHeight: 1.55, color: '#5A564D', maxWidth: 600, margin: '22px auto 0' }}>
        See how companies replaced hours of manual admin with automation that just works.
      </p>
    </section>

    <section style={{ maxWidth: 1000, margin: '0 auto', padding: '56px 40px 0' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 36 }}>
        {CASE_STUDIES.map(cs => (
          <article key={cs.id} style={{ background: '#fff', borderRadius: 24, padding: 48, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>

            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 9, background: cs.industryBg, color: cs.industryColor, borderRadius: 999, padding: '8px 16px', fontSize: 12, fontWeight: 700, letterSpacing: '0.05em', marginBottom: 18 }}>
              {cs.icon} {cs.industry}
            </div>
            <h2 style={{ fontSize: 30, fontWeight: 800, letterSpacing: '-0.02em', margin: '0 0 6px', lineHeight: 1.15, color: '#1A1A18' }}>{cs.title}</h2>
            <p style={{ fontSize: 16, fontWeight: 600, color: cs.industryColor, margin: '0 0 28px' }}>{cs.company}</p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 32 }}>
              {cs.results.map(r => (
                <div key={r.metric} style={{ background: '#F6F4EE', borderRadius: 16, padding: '22px 18px', textAlign: 'center' }}>
                  <div style={{ fontSize: 30, fontWeight: 800, letterSpacing: '-0.02em', color: '#C0492A' }}>{r.metric}</div>
                  <div style={{ fontSize: 12, lineHeight: 1.35, color: '#6E6A60', marginTop: 6 }}>{r.label}</div>
                </div>
              ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32 }}>
              <div>
                <h3 style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 14, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#C0492A', margin: '0 0 12px' }}>
                  <span style={{ width: 22, height: 22, borderRadius: '50%', background: '#F6E7E1', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flexShrink: 0 }}>!</span>
                  The Challenge
                </h3>
                <p style={{ fontSize: 15, lineHeight: 1.6, color: '#5A564D', margin: 0 }}>{cs.challenge}</p>
              </div>
              <div>
                <h3 style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 14, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#5E6B39', margin: '0 0 12px' }}>
                  <span style={{ width: 22, height: 22, borderRadius: '50%', background: '#EAEDE2', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flexShrink: 0 }}>✓</span>
                  Our Solution
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {cs.solution.map((sol, i) => (
                    <div key={i} style={{ display: 'flex', gap: 10, fontSize: 15, lineHeight: 1.5, color: '#3A3730' }}>
                      <span style={{ color: '#5E6B39', fontWeight: 800, flexShrink: 0 }}>→</span>
                      <span>{sol}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ background: '#1A1A18', color: '#fff', borderRadius: 18, padding: '30px 34px', marginTop: 32 }}>
              <p style={{ fontSize: 17, lineHeight: 1.6, fontStyle: 'italic', margin: '0 0 16px' }}>"{cs.quote}"</p>
              <div style={{ fontSize: 14, color: '#C5C0B6' }}>
                <strong style={{ color: '#fff' }}>{cs.author}</strong> — {cs.role}, {cs.company}
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>

    <section style={{ maxWidth: 1000, margin: '56px auto 0', padding: '0 40px 80px' }}>
      <div style={{ background: '#C0492A', color: '#fff', borderRadius: 28, padding: 56, textAlign: 'center' }}>
        <h2 style={{ fontSize: 'clamp(1.6rem, 2.5vw, 2.1rem)', fontWeight: 800, letterSpacing: '-0.02em', margin: 0 }}>
          Ready to see these results in your business?
        </h2>
        <p style={{ fontSize: 18, color: '#F3D9CE', margin: '14px 0 30px' }}>
          Book a free 30-minute discovery call and we'll show you exactly where automation can deliver the same impact.
        </p>
        <Link to="/#booking" style={{ display: 'inline-block', background: '#fff', color: '#1A1A18', fontSize: 17, fontWeight: 700, padding: '16px 32px', borderRadius: 13, textDecoration: 'none' }}>
          Book a free discovery call →
        </Link>
      </div>
    </section>
    <Footer />
  </div>
);

export default CaseStudies;
